import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { useRole } from "../context/RoleContext";
import { useCycle } from "../context/CycleContext";
import { VIEWS } from "../services/navigation";
import { getNotifications, markNotificationRead } from "../services/notificationApi";
import { getTeamCounters } from "../services/dashboardApi";
import { myRecordSummary, recordDetail } from "../services/core"; // [NEW] employee goal-sheet badge
import { countPendingProgress } from "../services/progressApprovalApi";
import SessionExpiryNotice from "./SessionExpiryNotice";
import { sessionIsOver } from "../services/api"; // [NEW] no background calls with an expired login
import ProductTour from "../components/tour/ProductTour";
import { Card, EmptyState, Loader } from "../components/common";
import { watchTables } from "../utils/autoTableColumns";
import useAutoReminders from "../hooks/useAutoReminders"; // [NEW] PM-10

const api = { getNotifications, getTeamCounters };

const CYCLE_FREE = ["/hr/cycles", "/hr/employees", "/hr/organisation", "/hr/kra-master", "/hr/kpi-master", "/hr/role-templates",
  "/hr/rating-settings", "/admin", "/audit-trail", "/monitoring", "/profile", "/notifications", "/pip", "/help"];

export default function AppLayout() {
  const { hasView, setCounters, profile } = useRole();
  useAutoReminders(profile); // [NEW] PM-10: run the automatic reminder rules once a day
  const { cycleId, cycles, loading: cyclesLoading, error: cyclesError } = useCycle();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const isManager = hasView(VIEWS.MANAGER);
  const seenProgress = useRef(null); // ids of PROGRESS_SUBMITTED notifications already announced
  // [NEW] "My goals" badge: remembers the goal-sheet decision (approved / changes requested) the employee has already seen.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const goalsRef = useRef({ cycleId: null, recordId: null, stamp: "" });
  const seenKey = (id) => `pms_goals_seen_${id}`;

  // [NEW] Size every table column to its content (see utils/autoTableColumns.js).
  useEffect(() => watchTables(document.body), []);

  const refreshCounters = useCallback(async () => {
    try {
      const { data: list } = await api.getNotifications();
      setNotifications(list);
      const next = { unread: list.filter((n) => !n.read).length };
      // [NEW] Unread notifications per menu page (a notification's link is the page it belongs to).
      next.unreadByLink = list.filter((n) => !n.read && n.link).reduce((acc, n) => {
        const path = n.link.split("?")[0];
        acc[path] = (acc[path] || 0) + 1;
        return acc;
      }, {});
      // [NEW] Pop-up for the manager when an employee sends a progress update (first load only remembers the ids).
      if (isManager) {
        const fresh = list.filter((n) => n.event === "PROGRESS_SUBMITTED" && !n.read);
        if (seenProgress.current) {
          fresh.filter((n) => !seenProgress.current.has(n.id)).forEach((n) => toast.info(`${n.message}`, { toastId: `progress-${n.id}` }));
        }
        seenProgress.current = new Set(fresh.map((n) => n.id));
      }
      if (isManager && cycleId) {
        const { data } = await api.getTeamCounters(cycleId);
        next.pendingApprovals = data.pendingApprovals;
        next.pendingAssessments = data.pendingAssessments;
        next.pendingProgress = (await countPendingProgress(cycleId)).count;
      }
      // [NEW] Employee: badge on "My goals" when the manager approved the goals or sent them back, until the page is opened.
      if (cycleId) {
        try {
          const g = goalsRef.current;
          if (g.cycleId !== cycleId || !g.recordId) {
            const summary = await myRecordSummary(cycleId);
            goalsRef.current = { cycleId, recordId: summary.performance_record_id, stamp: "" };
          }
          const plan = (await recordDetail(goalsRef.current.recordId)).goal_plan || {};
          const stamp = plan.approved_at
            || (plan.plan_status === "CHANGES_REQUESTED" ? (plan.updated_at || plan.submitted_at || "changes") : "");
          goalsRef.current.stamp = stamp ? String(stamp) : "";
          if (stamp && pathRef.current === "/my-goals") localStorage.setItem(seenKey(cycleId), String(stamp)); // already looking at it
          const seen = localStorage.getItem(seenKey(cycleId));
          next.myGoalsUpdate = stamp && String(stamp) !== seen ? 1 : 0;
        } catch {
          next.myGoalsUpdate = 0; // no record in this cycle, or the call failed
        }
      }
      setCounters((c) => ({ ...c, ...next }));
    } catch {
    }
  }, [isManager, cycleId, setCounters]);

  useEffect(() => {
    setNavOpen(false);
    // [NEW] Opening "My goals" clears its badge.
    if (location.pathname === "/my-goals" && goalsRef.current.cycleId) {
      if (goalsRef.current.stamp) localStorage.setItem(seenKey(goalsRef.current.cycleId), goalsRef.current.stamp);
      setCounters((c) => ({ ...c, myGoalsUpdate: 0 }));
    }
    // [NEW] Opening a page marks its unread notifications as read, so the sidebar count goes down.
    (async () => {
      try {
        const { data: list } = await api.getNotifications();
        const here = list.filter((n) => !n.read && n.link && n.link.split("?")[0] === location.pathname);
        await Promise.all(here.map((n) => markNotificationRead(n.id)));
      } catch {
      }
      refreshCounters();
    })();
  }, [location.pathname, refreshCounters, setCounters]);

  // [NEW] Look for new notifications / progress updates every 30 seconds and when the tab is focused again.
  // [UPDATED] Only while the tab is visible and the login is still valid (no calls with an expired session).
  useEffect(() => {
    const refreshIfActive = () => {
      if (document.visibilityState === "hidden" || sessionIsOver()) return;
      refreshCounters();
    };
    const timer = setInterval(refreshIfActive, 30000);
    window.addEventListener("focus", refreshIfActive);
    return () => { clearInterval(timer); window.removeEventListener("focus", refreshIfActive); };
  }, [refreshCounters]);

  const needsCycle = !CYCLE_FREE.some((p) => location.pathname.startsWith(p));
  let content = <Outlet context={{ refreshCounters }} />;
  if (needsCycle && cyclesLoading && !cycles.length) content = <Loader label="Loading performance cycles" />;
  else if (needsCycle && !cyclesLoading && !cycles.length) {
    content = (
      <div className="page">
        <Card>
          <EmptyState
            title={cyclesError ? "Couldn't load performance cycles" : "No performance cycle yet"}
            text={cyclesError || "You are not part of any performance cycle yet. HR creates a cycle and adds employees under Performance cycles."}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">Skip to content</a>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="app-main">
        <Topbar onMenu={() => setNavOpen(true)} notifications={notifications} onNotificationsChange={refreshCounters} />
        <main className="app-content" id="main">
          <SessionExpiryNotice />
          {content}
        </main>
      </div>
      <ProductTour />
    </div>
  );
}
